<script setup lang="ts">
import SegmentGroupOpacity from '@/src/components/SegmentGroupOpacity.vue';
import SegmentList from '@/src/components/SegmentList.vue';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import { useDatasetStore } from '@/src/store/datasets';
import {
  getSelectionName,
  selectionEquals,
  DataSelection,
} from '@/src/utils/dataSelection';
import { useSegmentGroupStore } from '@/src/store/segmentGroups';
import { useGlobalLayerColorConfig } from '@/src/composables/useGlobalLayerColorConfig';
import { usePaintToolStore } from '@/src/store/tools/paint';
import { computed, watch } from 'vue';
import { useMultiSelection } from '@/src/composables/useMultiSelection';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const segmentGroupStore = useSegmentGroupStore();
const { currentImageID } = useCurrentImage();
const dataStore = useDatasetStore();

const currentSegmentGroups = computed(() => {
  if (!currentImageID.value) return [];
  const { orderByParent, metadataByID } = segmentGroupStore;
  if (!(currentImageID.value in orderByParent)) return [];
  return orderByParent[currentImageID.value].map((id) => {
    const { sampledConfig, updateConfig } = useGlobalLayerColorConfig(id);
    return {
      id,
      name: metadataByID[id].name,
      visibility: sampledConfig.value?.config?.blendConfig.visibility ?? true,
      toggleVisibility: () => {
        const currentBlend = sampledConfig.value!.config!.blendConfig;
        updateConfig({
          blendConfig: {
            ...currentBlend,
            visibility: !currentBlend.visibility,
          },
        });
      },
    };
  });
});

const paintStore = usePaintToolStore();
const currentSegmentGroupID = computed({
  get: () => paintStore.activeSegmentGroupID,
  set: (id) => paintStore.setActiveSegmentGroup(id),
});

watch(currentSegmentGroups, () => {
  const selection = currentSegmentGroupID.value;
  if (selection && !(selection in segmentGroupStore.dataIndex)) {
    currentSegmentGroupID.value = null;
  }
});

const segmentGroupConvertibles = computed(() => {
  const primarySelection = dataStore.primarySelection;
  if (!primarySelection) return [];
  return dataStore.idsAsSelections
    .filter((selection) => !selectionEquals(selection, primarySelection))
    .map((selection) => ({
      selection,
      name: getSelectionName(selection),
    }));
});

function createSegmentGroupFromImage(selection: DataSelection) {
  const primarySelection = dataStore.primarySelection;
  if (!primarySelection) {
    throw new Error(t('annotations.noPrimarySelection'));
  }
  segmentGroupStore.convertImageToLabelmap(selection, primarySelection);
}

const segGroupIds = computed(() =>
  currentSegmentGroups.value.map((group) => group.id)
);

const { selected, selectedAll, selectedSome } = useMultiSelection(segGroupIds);

watch(
  [currentSegmentGroupID, currentImageID],
  () => {
    const groupId = currentSegmentGroupID.value;
    if (!groupId) return;
    selected.value = [groupId];
  },
  { immediate: true }
);

function toggleSelectAll() {
  if (selectedAll.value && currentSegmentGroupID.value) {
    selected.value = [currentSegmentGroupID.value];
  } else if (selectedAll.value) {
    selected.value = [];
  } else {
    selected.value = segGroupIds.value;
  }
}

const allHidden = computed(() => {
  return selected.value
    .map((id) => currentSegmentGroups.value.find((group) => id === group.id))
    .filter((group): group is NonNullable<typeof group> => group != null)
    .every((group) => !group.visibility);
});

function toggleGlobalVisibility() {
  const shouldShow = allHidden.value;
  selected.value.forEach((id) => {
    const group = currentSegmentGroups.value.find((g) => g.id === id);
    if (group) {
      const { sampledConfig, updateConfig } = useGlobalLayerColorConfig(id);
      const currentBlend = sampledConfig.value!.config!.blendConfig;
      updateConfig({
        blendConfig: {
          ...currentBlend,
          visibility: shouldShow,
        },
      });
    }
  });
}

function isAlreadyAdded(itemName: string | null | undefined) {
  if (!itemName) return false;
  const baseName = itemName.trim();
  return currentSegmentGroups.value.some((group) =>
    group.name.trim().startsWith(baseName)
  );
}
</script>

<template>
  <div class="mt-2" v-if="currentImageID">
    <div
      class="text-grey text-subtitle-2 d-flex align-center justify-space-evenly mb-2"
    >
      <v-menu location="bottom">
        <template v-slot:activator="{ props }">
          <v-btn
            variant="tonal"
            color="secondary"
            density="compact"
            v-bind="props"
          >
            <v-icon class="mr-1">mdi-chevron-down</v-icon
            >{{ t('annotations.fromImage') }}
          </v-btn>
        </template>
        <v-list v-if="segmentGroupConvertibles.length !== 0">
          <v-list-item
            v-for="(item, index) in segmentGroupConvertibles"
            :key="index"
            @click="
              !isAlreadyAdded(item.name) &&
                createSegmentGroupFromImage(item.selection)
            "
            :disabled="isAlreadyAdded(item.name)"
          >
            {{ item.name }}
            <v-tooltip activator="parent" location="end" max-width="200px">
              <span v-if="isAlreadyAdded(item.name)">{{
                t('annotations.alreadyAdded')
              }}</span>
              <span v-else>{{ t('annotations.addAsSegmentGroup') }}</span>
            </v-tooltip>
          </v-list-item>
        </v-list>
        <v-list v-else>
          <v-list-item
            class="font-italic"
            :title="t('annotations.noEligibleImages')"
          />
        </v-list>
      </v-menu>
    </div>

    <segment-group-opacity
      v-if="currentSegmentGroupID"
      :group-id="currentSegmentGroupID"
      :selected="selected"
    />

    <div class="d-flex align-center" v-if="currentSegmentGroups.length > 0">
      <v-checkbox
        class="ml-3"
        :indeterminate="selectedSome && !selectedAll"
        :label="t('annotations.selectAll')"
        :model-value="selectedAll"
        @update:model-value="toggleSelectAll"
        density="compact"
        hide-details
      />
      <v-btn
        icon
        variant="text"
        :disabled="selected.length === 0"
        @click.stop="toggleGlobalVisibility"
      >
        <v-icon v-if="allHidden">mdi-eye-off</v-icon>
        <v-icon v-else>mdi-eye</v-icon>
        <v-tooltip location="top" activator="parent">
          {{ allHidden ? t('annotations.show') : t('annotations.hide') }}
          {{ t('annotations.selected') }}
        </v-tooltip>
      </v-btn>
    </div>
    <v-list density="comfortable" class="my-1 segment-group-list">
      <v-list-item
        v-for="group in currentSegmentGroups"
        :key="group.id"
        :active="currentSegmentGroupID === group.id"
        @click="currentSegmentGroupID = group.id"
      >
        <div class="d-flex flex-row align-center w-100" :title="group.name">
          <v-checkbox
            class="no-grow mr-4"
            density="compact"
            hide-details
            @click.stop
            :value="group.id"
            v-model="selected"
            :disabled="group.id === currentSegmentGroupID"
          />
          <span class="group-name">{{ group.name }}</span>
          <v-spacer />
          <v-btn
            icon
            variant="text"
            size="small"
            @click.stop="group.toggleVisibility"
          >
            <v-icon v-if="group.visibility" style="pointer-events: none"
              >mdi-eye</v-icon
            >
            <v-icon v-else style="pointer-events: none">mdi-eye-off</v-icon>
            <v-tooltip location="left" activator="parent">
              {{ group.visibility ? t('annotations.hide') : t('annotations.show') }}
            </v-tooltip>
          </v-btn>
        </div>
      </v-list-item>
      <v-list-item v-if="currentSegmentGroups.length === 0">
        <div class="text-center text-grey-darken-1 py-4 w-100">
          {{ t('annotations.createSegmentGroupHint') }}
        </div>
      </v-list-item>
    </v-list>

    <v-divider class="my-4" />
  </div>
  <div v-else class="empty-state ma-4 text-center">
    {{ t('annotations.noSelectedImage') }}
  </div>
  <segment-list
    v-if="currentSegmentGroupID"
    :group-id="currentSegmentGroupID"
  />
</template>

<style>
.segment-group-list {
  max-height: 240px;
  overflow-y: auto;
}

.group-name {
  word-wrap: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 10px;
  text-align: left;
}

.no-grow {
  flex: 0 0 auto;
}
</style>